import { Box, Button, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookingTypeEnum } from '../../redux/booking/bookingSlice';
import { formatDuration } from '../../utils/utils';

export interface IMiniWebSessionCardProps {
  title: string;
  bookingType: BookingTypeEnum;
  price: number;
  description: string;
  duration: number;
  onBookNowClick: () => void;
  noCalendar?: boolean;
  noVideoCallPlatform?: boolean;
  username?: string;
  noAvailableSlots?: boolean;
  isProvideOnlineConsultations?: boolean;
  isProvideInPersonConsultations?: boolean;
}
export default function MiniWebSessionCard({
  title,
  bookingType,
  price,
  description,
  duration,
  onBookNowClick,
  noCalendar,
  noVideoCallPlatform,
  username,
  noAvailableSlots,
  isProvideOnlineConsultations,
  isProvideInPersonConsultations,
}: IMiniWebSessionCardProps) {
  const { t } = useTranslation();

  const isOnlineSessionWithNoPlatform =
    bookingType === BookingTypeEnum.ONLINE && noVideoCallPlatform;

  const isContact = () => {
    if (noCalendar) {
      return true;
    }

    if (isOnlineSessionWithNoPlatform) {
      return true;
    }

    if (noAvailableSlots) {
      return true;
    }

    if (
      (isProvideOnlineConsultations === false && bookingType === BookingTypeEnum.ONLINE) ||
      (isProvideInPersonConsultations === false && bookingType === BookingTypeEnum.IN_PERSON)
    ) {
      return true;
    }

    return false;
  };

  const buttonText = isContact()
    ? t('miniWebSite.contact', { username })
    : t('miniWebSite.bookNow');

  return (
    <Box
      sx={{
        width: '373px',
        height: '400px',
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 4px 40px 0px #FFDDC840',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          width: '100%',
          minHeight: '95px',
          borderRadius: '10px 10px 0px 0px',
          padding: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #EEEEEE',
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <Tooltip
            title={title}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'primary.main',
                  borderRadius: '5px',
                  padding: '8px 12px',
                },
              },
            }}
          >
            <Typography
              variant="title2Bold"
              color="primary"
              textTransform="capitalize"
              sx={{
                cursor: 'pointer',
                display: '-webkit-box',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
              }}
            >
              {title}
            </Typography>
          </Tooltip>
          <Box display="flex" alignItems="center">
            <img
              src="/assets/icons/clock-session.svg"
              alt="time"
              width={18}
              height={18}
              style={{ marginRight: '4px', flexShrink: 0 }}
            />
            <Typography
              variant="bodyBold"
              color="primary"
              fontSize="15px"
              style={{
                display: 'inline',
                flexGrow: 1,
                wordBreak: 'break-word',
                whiteSpace: 'normal',
              }}
            >
              {formatDuration(duration)} -{' '}
              {bookingType === BookingTypeEnum.ONLINE
                ? t('bookings.online')
                : t('bookings.inPerson')}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            alignSelf: 'flex-start',
          }}
        >
          <img src="/assets/icons/session-price.svg" alt="price" />
          <Typography variant="h6" color="primary">
            ${price}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '20px 16px',
          borderRadius: '0px 0px 10px 10px',
          gap: '20px',
          flexGrow: 1,
        }}
      >
        <Box
          sx={{
            height: '155px',
            overflowY: 'auto',
            overflowX: 'hidden',
            wordBreak: 'break-word',
            '&::-webkit-scrollbar': {
              width: '4px',
              height: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#6CA3AC',
              borderRadius: '10px',
              border: '2px solid transparent',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#4C7A83',
            },
            '&::-webkit-scrollbar-thumb:active': {
              backgroundColor: '#365A5F',
            },
            '&::-webkit-scrollbar-button': {
              display: 'none',
            },
          }}
        >
          <Typography variant="bodyRegular" color="primary">
            {description}
          </Typography>
        </Box>
        <Button
          variant="contained"
          sx={{
            background:
              'var(--Brand-Cadet-Blue, linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%))',
            borderRadius: '15px',
            '&:hover': {
              background:
                'var(--Brand-Cadet-Blue, linear-gradient(138deg, #4e7c91 24.11%, #2d4f5f 83.2%))',
            },
            marginTop: 'auto',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
          fullWidth
          onClick={onBookNowClick}
          disabled={isContact()}
        >
          {buttonText}
        </Button>
      </Box>
    </Box>
  );
}
